<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 z-[9999]"
      @click.self="handleClose"
    >
      <div
        class="bg-white rounded-2xl my-4 shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black max-w-xl w-full transform transition-all"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 pt-4">
          <h2 class="text-2xl font-bold text-gray-900">{{ displayTitle }}</h2>
          <button
            @click="handleClose"
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>

        <!-- Content -->
        <div class="p-6 overflow-y-auto flex-1">
          <!-- Selected Items -->
          <div class="mb-6">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">Selected ({{ selectedLanguages.length }})</h3>
            <div v-if="selectedLanguages.length > 0" class="flex flex-wrap gap-2">
              <div
                v-for="language in selectedLanguages"
                :key="language"
                class="group flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-800 rounded-full border-2 border-blue-200 hover:bg-blue-200 transition-colors"
              >
                <span class="text-sm font-medium">{{ language }}</span>
                <button
                  @click="removeLanguage(language)"
                  class="text-blue-600 hover:text-blue-800"
                  title="Remove"
                >
                  <XCircleIcon class="w-5 h-5" />
                </button>
              </div>
            </div>
            <p v-else class="text-sm text-gray-500 italic">Nothing selected yet</p>
          </div>

          <!-- Search Box -->
          <div class="mb-4">
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              Add Languages
            </label>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search..."
              class="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
            />
          </div>

          <!-- Available Languages -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 bg-gray-50 rounded-lg border-2 border-gray-200">
            <button
              v-for="language in filteredAvailableLanguages"
              :key="language"
              @click="addLanguage(language)"
              class="flex items-center gap-2 px-4 py-3 bg-white text-gray-700 rounded-lg border-2 border-gray-300 hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
            >
              <PlusIcon class="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
              <span class="text-sm font-medium">{{ language }}</span>
            </button>
            <p v-if="filteredAvailableLanguages.length === 0" class="col-span-2 text-center text-gray-500 py-4">
              {{ searchQuery ? 'No languages found' : 'All languages selected' }}
            </p>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 p-6">
          <button
            @click="handleClose"
            class="px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            @click="handleSave"
            :disabled="!isValid || isSaving"
            class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-md flex items-center gap-2"
          >
            <svg v-if="isSaving" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>{{ isSaving ? 'Saving...' : 'Save Changes' }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { XMarkIcon, PlusIcon, XCircleIcon } from '@heroicons/vue/24/outline';

interface Props {
  isOpen: boolean;
  items: string[];
  title?: string;
  isSaving?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save', languages: string[]): void;
}>();

const availableLanguages = [
  'English',
  'Filipino',
  'Mandarin Chinese',
  'Spanish',
  'Japanese',
  'Korean',
  'French',
  'German',
  'Italian',
  'Portuguese',
  'Russian',
  'Arabic',
  'Hindi',
  'Bengali',
  'Indonesian',
  'Vietnamese',
  'Thai',
  'Other'
];

const selectedLanguages = ref<string[]>([]);
const searchQuery = ref('');

const displayTitle = computed(() => props.title || 'Edit Languages');

const isValid = computed(() => selectedLanguages.value.length > 0);

const filteredAvailableLanguages = computed(() => {
  const query = searchQuery.value.toLowerCase();
  return availableLanguages.filter(
    (language) =>
      !selectedLanguages.value.includes(language) &&
      language.toLowerCase().includes(query)
  );
});

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    selectedLanguages.value = [...props.items];
    searchQuery.value = '';
  }
});

const addLanguage = (language: string) => {
  if (!selectedLanguages.value.includes(language)) {
    selectedLanguages.value.push(language);
    searchQuery.value = '';
  }
};

const removeLanguage = (language: string) => {
  selectedLanguages.value = selectedLanguages.value.filter((l) => l !== language);
};

const handleSave = () => {
  if (!isValid.value) return;
  
  emit('save', selectedLanguages.value);
};

const handleClose = () => {
  emit('close');
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.9);
}
</style>